// IntroSuggestions.jsx
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, RefreshCcw, Send, ArrowRight, EyeOff } from "lucide-react";
import "./IntroSuggestions.css";

const STATIC_INTROS = {
  intros: [
    {
      user_id: "user-2",
      member_code: "M-2847",
      title: "Founder & CEO",
      company: "Greentide",
      rationale:
        "You're both building in climate-tech and looking to connect with institutional capital. Their carbon market expertise complements your renewable energy focus.",
      seeking: "Institutional LPs for climate & energy transition.",
      giving:
        "Deep expertise in carbon markets and renewable energy project finance.",
    },
    {
      user_id: "user-6",
      member_code: "M-5192",
      title: "Policy Advisor",
      company: "UN Climate",
      rationale:
        "Policy meets capital. They need connections to climate-tech founders — you're looking for policy guidance for your portfolio companies.",
      seeking:
        "Connections to climate-tech founders and institutional capital.",
      giving: "Regulatory strategy and emerging market policy frameworks.",
    },
    {
      user_id: "user-8",
      member_code: "M-3490",
      title: "Founder",
      company: "Nexa AI",
      rationale:
        "You invest in AI at seed stage; they're raising a pre-seed round. Their enterprise GTM angle aligns with your portfolio thesis.",
      seeking: "Seed funding and enterprise GTM advisors.",
      giving: "Open-source AI framework and developer community access.",
    },
    {
      user_id: "user-4",
      member_code: "M-7621",
      title: "Partner",
      company: "Elevate VC",
      rationale:
        "You're both active in climate investing with complementary geographic focus — you in Europe, they in Southeast Asia. Natural co-investment potential.",
      seeking: "Pre-seed and seed-stage founders in climate and deep tech.",
      giving: "LP access across European and Asian institutional networks.",
    },
    {
      user_id: "user-5",
      member_code: "M-1283",
      title: "Head of Design",
      company: "Stripe",
      rationale:
        "Their design leadership at scale matches your portfolio companies' need for design talent and creative strategy.",
      seeking: "Design talent and creative collaborators.",
      giving: "Design systems mentorship and hiring playbooks.",
    },
    {
      user_id: "user-7",
      member_code: "M-9045",
      title: "Angel Investor",
      company: "Kim Capital",
      rationale:
        "You're both looking at AI and robotics at pre-seed. Their angel network in APAC complements your US deal flow.",
      seeking: "AI and robotics startups at pre-seed.",
      giving: "Cross-border deal flow and Asian market entry intros.",
    },
  ],
  cached: true,
  status: "available",
};

export default function IntroSuggestions() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [requested, setRequested] = useState({});

  const load = async (refresh = 0) => {
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, refresh ? 1200 : 600));
    try {
      setData(STATIC_INTROS);
    } catch {
      setData({ intros: [] });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(0);
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    await load(1);
  };

  const requestIntro = async (userId) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    try {
      setRequested((r) => ({ ...r, [userId]: true }));
      toast.success("Intro request sent — the member will see who you are.");
    } catch {
      toast.error("Failed.");
    }
  };

  /* Loading skeleton */
  if (loading && !data) {
    return (
      <div className="glass introsLoading" data-testid="intros-loading">
        <p className="overline introsLoadingOverline">
          <Sparkles size={12} /> vmeetio matcher · curating your intros…
        </p>
      </div>
    );
  }

  /* Unavailable state */
  if (data && data.status === "unavailable") {
    return (
      <div className="glass introsUnavailable" data-testid="intros-unavailable">
        <Sparkles size={16} className="introsUnavailableIcon" />
        <div className="introsUnavailableBody">
          <p className="overline introsUnavailableOverline">
            vmeetio matcher · temporarily unavailable
          </p>
          <p className="introsUnavailableText">
            The AI matcher is taking a breath. We're caching your seeking /
            giving — your curated intros will be ready as soon as the matcher is
            back.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="introsRetryBtn"
          data-testid="intros-refresh"
        >
          <RefreshCcw size={12} className={refreshing ? "introsSpin" : ""} />{" "}
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.intros.length === 0) return null;

  return (
    <div className="introsSection" data-testid="intros-section">
      {/* Header */}
      <div className="introsHeader">
        <div>
          <p className="overline introsHeaderOverline">
            <Sparkles size={12} /> vmeetio matcher · curated by AI
          </p>
          <h3 className="introsHeading">People you should meet.</h3>
          <p className="introsAnonNote">
            <EyeOff size={11} /> Names are hidden — request an intro to reveal
            who they are.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="introsRefreshBtn"
          data-testid="intros-refresh"
        >
          <RefreshCcw size={12} className={refreshing ? "introsSpin" : ""} />
          {refreshing
            ? "Re-curating…"
            : data.cached
              ? "Refresh suggestions"
              : "Just refreshed"}
        </button>
      </div>

      {/* Cards */}
      <div className="introsGrid">
        {data.intros.map((i) => (
          <div
            key={i.user_id}
            data-testid={`intro-card-${i.user_id}`}
            className="glass introCard"
          >
            {/* Identity */}
            <div className="introCardIdentity">
              <h4 className="introCardName">
                <span className="introCardAnonChip">
                  <EyeOff size={14} className="introCardAnonIcon" />
                </span>
                Member {i.member_code}
              </h4>
              {(i.title || i.company) && (
                <p className="overline introCardRole">
                  {[i.title, i.company].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>

            {/* Rationale */}
            <p className="introCardRationale">"{i.rationale}"</p>

            {/* Seeking / Giving */}
            <div className="introCardDetails">
              {i.seeking && (
                <p className="introCardDetailRow">
                  <span className="introCardDetailKey">Seeking · </span>
                  {i.seeking}
                </p>
              )}
              {i.giving && (
                <p className="introCardDetailRow">
                  <span className="introCardDetailKey">Giving · </span>
                  {i.giving}
                </p>
              )}
            </div>

            {/* Action */}
            {requested[i.user_id] ? (
              <span
                className="overline introCardSent"
                data-testid={`intro-sent-${i.user_id}`}
              >
                <ArrowRight size={11} /> Intro requested — they'll see who you
                are
              </span>
            ) : (
              <button
                onClick={() => requestIntro(i.user_id)}
                data-testid={`intro-request-${i.user_id}`}
                className="introCardRequestBtn"
              >
                <Send size={11} /> Request intro
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
